import React, { useState, useEffect } from 'react';
import {
Box,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Menu,
MenuItem,
Paper,
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
TablePagination,
TextField,
Typography,
useMediaQuery,
useTheme,
Chip,
Avatar,
Grid,
FormControl,
InputLabel,
Select
} from '@mui/material';
import axios from 'axios';
import {
Close as CloseIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
MoreVert as MoreIcon,
Delete as DeleteIcon,
Visibility as ViewIcon,
Email as EmailIcon,
Business as BusinessIcon,
Subject as SubjectIcon,
Message as MessageIcon,
Schedule as ScheduleIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../hooks/useToast';
import { APIContactUsDelete, APIContactUsList, APIContactUsUpdate, headers } from '../utilities/APIs';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import { formatCreatedAtDate, formatNumberWithComma } from '../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const ContactUs = () => {
const [postStatus, setPostStatus] = useState('ADD');
const [updateId, setUpdateId] = useState('');
const theme = useTheme();
const showToast = useCustomToast();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State for table data and pagination
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(25);
const [searchTerm, setSearchTerm] = useState('');
const [filters, setFilters] = useState({
    status: '',
    name: '',
    email: ''
});

// State for dialogs
const [openOptions, setOpenOptions] = useState(false);
const [openView, setOpenView] = useState(false);
const [openStatus, setOpenStatus] = useState(false);
const [openDelete, setOpenDelete] = useState(false);

const [pageState, setPageState] = useState({
    pageData: []
});

// Selected contact state
const [selectedContact, setSelectedContact] = useState(null);

// Menu state
const [anchorEl, setAnchorEl] = useState(null);
const openMenu = Boolean(anchorEl);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APIContactUsList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];

    const contactsWithStatus = data.map((contact) => ({
        ...contact,
        status: contact.status || 'pending'
    }));

    setPageState((prev) => ({
        ...prev,
        pageData: contactsWithStatus
    }));
    } catch (error) {
    let errorMessage = 'Failed to load data';
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === 'ERR_NETWORK') {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast('Error', errorMessage, 'error');
    } finally {
    setLoading(false);
    }
};

// Filter contacts based on search term and filters
const filteredContacts = pageState.pageData.filter((contact) => {
    const matchesSearch =
    searchTerm === '' ||
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !filters.status || contact.status === filters.status;
    const matchesName = !filters.name || contact.name?.toLowerCase().includes(filters.name.toLowerCase());
    const matchesEmail = !filters.email || contact.email?.toLowerCase().includes(filters.email.toLowerCase());

    return matchesSearch && matchesStatus && matchesName && matchesEmail;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handleOpenOptions = () => setOpenOptions(true);
const handleCloseOptions = (event, reason) => {
    if (reason === 'backdropClick') return;
    setOpenOptions(false);
};

const handleOpenView = (contact) => {
    setSelectedContact(contact);
    setOpenView(true);
};
const handleCloseView = (event, reason) => {
    if (reason === 'backdropClick') return;
    setOpenView(false);
};

const handleOpenStatus = (contact) => {
    setSelectedContact(contact);
    setUpdateId(contact.id);
    setOpenStatus(true);
};
const handleCloseStatus = (event, reason) => {
    if (reason === 'backdropClick') return;
    setOpenStatus(false);
};

const handleOpenDelete = (contact) => {
    setSelectedContact(contact);
    setUpdateId(contact.id);
    setPostStatus('DELETE');
    setOpenDelete(true);
};
const handleCloseDelete = (event, reason) => {
    if (reason === 'backdropClick') return;
    setOpenDelete(false);
};

// Menu handlers
const handleMenuClick = (event, contact) => {
    setAnchorEl(event.currentTarget);
    setSelectedContact(contact);
};
const handleMenuClose = () => {
    setAnchorEl(null);
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
    ...prev,
    [name]: value
    }));
};

const clearFilters = () => {
    setFilters({
    status: '',
    name: '',
    email: ''
    });
    setSearchTerm('');
    setOpenOptions(false);
};

// Status update handler
const handleStatusUpdate = async (newStatus) => {
    try {
    const formData = new FormData();
    formData.append('status', newStatus);
    formData.append('updateId', updateId);

    const response = await axios.post(APIContactUsUpdate, formData, { headers });

    if (response.data.status === 'success') {
        showToast('Success', 'Status updated successfully', 'success');
        setOpenStatus(false);
        fetchData();
    } else {
        showToast('Error', 'Failed to update status', 'error');
    }
    } catch (error) {
    showToast('Error', 'Failed to update status', 'error');
    }
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== 'DELETE') return;

    const formDataToSend = new FormData();
    formDataToSend.append('updateId', updateId);

    try {
    const response = await axios.post(APIContactUsDelete, formDataToSend, { headers });

    if (response.data.status === 'success') {
        setOpenDelete(false);
        fetchData();
        showToast('Success', 'Contact message deleted successfully', 'success');
    } else {
        showToast('Error', 'Failed to delete contact message', 'error');
    }
    } catch (error) {
    showToast('Error', 'Failed to delete contact message', 'error');
    }
};

const getStatusColor = (status) => {
    switch (status) {
    case 'pending':
        return 'warning';
    case 'seen':
        return 'info';
    case 'finished':
        return 'success';
    default:
        return 'default';
    }
};

const getStatusText = (status) => {
    switch (status) {
    case 'pending':
        return 'Pending';
    case 'seen':
        return 'Seen';
    case 'finished':
        return 'Finished';
    default:
        return status;
    }
};

if (loading) return <div>Loading...</div>;
if (error) return showToast('Error', error, 'error');

return (
    <Box sx={{ marginTop: 5, flexGrow: 1, p: isMobile ? 1 : 3 }}>
    {/* Header Section */}
    <Box
        sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" component="h1" fontWeight="bold">
            Contact Messages
        </Typography>
        <Chip
            label={formatNumberWithComma(pageState.pageData.length)}
            color="primary"
            variant="outlined"
        />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={handleOpenOptions}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
        >
            Filters
        </Button>
        <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
        >
            Refresh
        </Button>
        </Box>
    </Box>

    {/* Search Bar */}
    <Box sx={{ mb: 3 }}>
        <Paper sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}>
        <TextField
            fullWidth
            variant="outlined"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            sx: { border: 'none', '& fieldset': { border: 'none' } }
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
        </Paper>
    </Box>

    {/* Active Filters Display */}
    {(filters.status || filters.name || filters.email) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {filters.status && (
            <Chip
            label={`Status: ${getStatusText(filters.status)}`}
            onDelete={() => setFilters((prev) => ({ ...prev, status: '' }))}
            color="primary"
            variant="outlined"
            />
        )}
        {filters.name && (
            <Chip
            label={`Name: ${filters.name}`}
            onDelete={() => setFilters((prev) => ({ ...prev, name: '' }))}
            color="primary"
            variant="outlined"
            />
        )}
        {filters.email && (
            <Chip
            label={`Email: ${filters.email}`}
            onDelete={() => setFilters((prev) => ({ ...prev, email: '' }))}
            color="primary"
            variant="outlined"
            />
        )}
        <Button size="small" onClick={clearFilters} startIcon={<CloseIcon />}>
            Clear All
        </Button>
        </Box>
    )}

    {/* Contacts Table */}
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: theme.shadows[3] }}>
        <Table sx={{ minWidth: 650 }} aria-label="Contacts table">
            <TableHead sx={{ backgroundColor: theme.palette.primary.main }}>
            <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
            {filteredContacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact, index) => (
                <TableRow
                    key={contact.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                        {contact.name}
                    </Typography>
                    </TableCell>
                    <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon color="action" fontSize="small" />
                        <Typography variant="body2">{contact.email}</Typography>
                    </Box>
                    </TableCell>
                    <TableCell>
                    <Chip
                        label={getStatusText(contact.status)}
                        color={getStatusColor(contact.status)}
                        size="small"
                        variant={contact.status === 'pending' ? 'filled' : 'outlined'}
                    />
                    </TableCell>
                    <TableCell>
                    <Typography variant="body2">
                        {formatCreatedAtDate(contact.created_at)}
                    </Typography>
                    </TableCell>
                    <TableCell>
                    <IconButton
                        aria-label="more"
                        aria-controls="long-menu"
                        aria-haspopup="true"
                        onClick={(e) => handleMenuClick(e, contact)}
                    >
                        <MoreIcon />
                    </IconButton>
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
        </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredContacts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ mt: 2 }}
        />
    </motion.div>

    {/* Filter Options Dialog */}
    <Dialog
        open={openOptions}
        onClose={handleCloseOptions}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="seen">Seen</MenuItem>
                <MenuItem value="finished">Finished</MenuItem>
            </Select>
            </FormControl>

            <TextField
            fullWidth
            label="Name"
            name="name"
            value={filters.name}
            onChange={handleFilterChange}
            />

            <TextField
            fullWidth
            label="Email"
            name="email"
            value={filters.email}
            onChange={handleFilterChange}
            />

            <Button
            variant="outlined"
            color="primary"
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Clear All Filters
            </Button>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setOpenOptions(false)} color="error">
            Cancel
        </Button>
        <Button onClick={() => setOpenOptions(false)} color="primary" variant="contained">
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* View Contact Details Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        fullWidth
        maxWidth="md"
    >
        <DialogTitle sx={{ textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" fontWeight="bold">
            Contact Message Details
        </Typography>
        </DialogTitle>
        <DialogContent>
        {selectedContact && (
            <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    {selectedContact.name?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                    <Typography variant="h6" fontWeight="bold">
                        {selectedContact.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedContact.email}
                    </Typography>
                    </Box>
                </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                    label={getStatusText(selectedContact.status)}
                    color={getStatusColor(selectedContact.status)}
                    size="medium"
                    />
                </Box>
                </Grid>

                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon color="action" />
                    <Typography variant="subtitle1" fontWeight="bold">
                    Company:
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4, mb: 2 }}>
                    {selectedContact.company}
                </Typography>
                </Grid>

                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <SubjectIcon color="action" />
                    <Typography variant="subtitle1" fontWeight="bold">
                    Subject:
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4, mb: 2 }}>
                    {selectedContact.subject}
                </Typography>
                </Grid>

                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MessageIcon color="action" />
                    <Typography variant="subtitle1" fontWeight="bold">
                    Message:
                    </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, ml: 4, backgroundColor: 'grey.50' }}>
                    <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedContact.message}
                    </Typography>
                </Paper>
                </Grid>

                <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                    Received: {formatCreatedAtDate(selectedContact.created_at)}
                    </Typography>
                </Box>
                </Grid>
            </Grid>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setOpenView(false)} color="primary">
            Close
        </Button>
        <Button
            onClick={() => {
            setOpenView(false);
            handleOpenStatus(selectedContact);
            }}
            color="primary"
            variant="contained"
        >
            Update Status
        </Button>
        </DialogActions>
    </Dialog>

    {/* Update Status Dialog */}
    <Dialog
        open={openStatus}
        onClose={handleCloseStatus}
        fullWidth
        maxWidth="xs"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>
        Update Message Status
        </DialogTitle>
        <DialogContent>
        <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
            Update status for message from <strong>{selectedContact?.name}</strong>
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
                variant={selectedContact?.status === 'pending' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => handleStatusUpdate('pending')}
                fullWidth
            >
                Mark as Pending
            </Button>

            <Button
                variant={selectedContact?.status === 'seen' ? 'contained' : 'outlined'}
                color="info"
                onClick={() => handleStatusUpdate('seen')}
                fullWidth
            >
                Mark as Seen
            </Button>

            <Button
                variant={selectedContact?.status === 'finished' ? 'contained' : 'outlined'}
                color="success"
                onClick={() => handleStatusUpdate('finished')}
                fullWidth
            >
                Mark as Finished
            </Button>
            </Box>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setOpenStatus(false)} color="error">
            Cancel
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Delete Message</DialogTitle>
        <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to delete this contact message?
        </Typography>
        {selectedContact && (
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            {selectedContact.name}
            </Typography>
        )}
        </DialogContent>
        <DialogActions>
        <Button onClick={() => setOpenDelete(false)} color="error">
            Cancel
        </Button>
        <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
        >
            Confirm Delete
        </Button>
        </DialogActions>
    </Dialog>

    {/* Actions Menu */}
    <Menu
        id="long-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{ style: { width: '200px' } }}
    >
        <MenuItem
        onClick={() => {
            handleOpenView(selectedContact);
            handleMenuClose();
        }}
        >
        <ViewIcon sx={{ mr: 1 }} /> View Details
        </MenuItem>

        <MenuItem
        onClick={() => {
            handleOpenStatus(selectedContact);
            handleMenuClose();
        }}
        >
        <EmailIcon sx={{ mr: 1 }} /> Update Status
        </MenuItem>

        {hasPermission(PERMISSIONS.DELETE) && (
        <MenuItem
            onClick={() => {
            handleOpenDelete(selectedContact);
            handleMenuClose();
            }}
            sx={{ color: theme.palette.error.main }}
        >
            <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
        )}
    </Menu>
    </Box>
);
};

export default ContactUs;